<template>
    <div>
        <div :class="['course-info', theme]">
            <div class="course-info__item -categories">
                <span class="icon-books"></span>
                <span class="course-info__label" v-html="$t('General.categories')"></span>
                <span class="course-info__data -clickable" v-for="(category, index) in categories"
                    :key="index"
                    @click="goToListWithPreFilteredOption(category, 'categories')"
                >
                    <span>{{ category.label }}</span>
                    <template v-if="index < categories.length - 1">,</template>
                </span>
            </div>

            <div class="course-info__item -level">
                <span class="icon-stack"></span>
                <span class="course-info__label" v-html="$t('General.level')"></span>
                <span class="course-info__data -clickable" @click="goToListWithPreFilteredOption(levelObject, 'difficulty_levels')" v-html="$t(`Select.skill_level_${level}`)"></span>
            </div>

            <span v-if="isTestAvailable" class="course-info__coursetest">
                 <span class="icon-file-text"></span>
                 <span v-html="$t('General.has_coursetest')"></span>
            </span>

            <div class="course-info__item -rating" v-if="theme === 'highlight' && enableRating">
                <TotemButton
                    icon="star-full"
                    size="small"
                    color="dark"
                    @click="showRatingModal"
                    :class="['course-info__rating-button', {'pulse-animation': !ratingSaved }]"
                    :label="ratingButtonLabel"
                />
            </div>
        </div>

        <div :class="['course-info', theme]" v-if="showCertificatesOrDurations">
            <span class="course-info__badges" v-if="shouldShowCertificates && certifiedByLength > 0">
                <TotemCertificationBadge
                    v-for="(cert, index) in certifiedByObjectArray"
                    :name="cert.label"
                    :key="'certs-index' + index"
                    size="small"
                    @click.native="goToListWithPreFilteredOption(cert, 'authorizations')"
                />
            </span>

            <span class="course-info__authorization-hours" v-if="durations && durations.length > 0">
                <VTooltip>
                    <TotemCertificationBadge
                        :name="$t('AuthorizationHours.title')"
                        size="small"
                    />
                    <template #popper>
                        <TotemAuthorizationHours :durations="durations" />
                    </template>
                </VTooltip>

                <TotemAuthorizationId
                    :id="authorizationId"
                    v-if="authorizationId"
                />
            </span>
        </div>
        <ModalCourseRating
            :courseTitle="title"
            :courseId="course.id"
            @ratingSaved="showToast"
        />
        <TotemToast ref="toast" />
    </div>
</template>

<script>
    import goToListWithPreFilteredOption from 'components/shared/mixins/goToListWithPreFilteredOption'
    import ModalCourseRating from 'components/shared/modals/ModalCourseRating'
    import courseRatingHelper from 'components/shared/global-courses-items/helper/course-rating-helper'
    import { mapGetters } from 'vuex'

    export default {
        name: 'totem-course-info',
        components: { ModalCourseRating },
        mixins: [ goToListWithPreFilteredOption, courseRatingHelper ],
        props: {
            categories: {
                type: Array,
                default: () => []
            },
            level: [ Number, String ],
            isLive: Boolean,
            liveEventDate: String,
            liveEventTime: String,
            authorizationId: String,
            certifiedBy: Array,
            durations: Array,
            title: String,
            theme: {
                type: String,
                validator: (prop) => [ 'basic', 'highlight' ].includes(prop),
                default: 'basic'
            },
            isTestAvailable: Boolean,
            course: {
                type: Object
            }
        },
        computed: {
            ...mapGetters({
                ratingSaved: 'Ratings/getRatingSavedState'
            }),
            shouldShowCertificates () {
                return this.$totemLang !== 'se'
            },
            certificationBadgeSize () {
                return this.theme === 'basic' ? 'small' : 'medium'
            },
            levelObject () {
                return {
                    id: this.level,
                    label: this.level
                }
            },
            certifiedByObjectArray () {
                return this.certifiedBy.map(el => ({
                    id: el.label,
                    label: el.label
                }))
            },
            certifiedByLength () {
                return this.certifiedBy?.length
            },
            showCertificatesOrDurations () {
                return (this.shouldShowCertificates && this.certifiedByLength > 0) || (this.durations && this.durations.length > 0)
            },
            ratingButtonLabel () {
                return this.ratingSaved ? this.$t('Rating.edit_your_rating_button') : this.$t('Rating.rate_this_course_button')
            }
        },
        methods: {
            showRatingModal () {
                this.$modal.show('course-rating')
            },
            showToast () {
                this.$refs.toast.show(this.$t('Rating.thank_you_message'))
            },
        }
    }
</script>

<style lang="scss" scoped>
    .course-info {
        display: inline;
        font-size: 1.5rem;
        line-height: 2rem;
        padding: 2px 0;
        pointer-events: none;

        .icon-books,
        .icon-stack {
            display: none;
        }

        &__item {
            margin: 0 10px 0 0;
            cursor: pointer;
            display: inline;
            align-items: center;

            &.-categories {
                flex-wrap: wrap;
                position: relative;
                flex: 1;
            }

            &.-level {
                position: relative;
            }
        }

        &__label {
            padding: 0 3px 0 0;
        }

        &__data {
            font-family: Nunito-ExtraBold, sans-serif;
            padding: 0 4px 0 0;
            position: relative;

            &.-clickable {
                cursor: pointer;
            }
        }

        &__category-name {
            margin: 0 0 0 5px;
        }

        &__description {
            font-size: 1.5rem;
            margin: 0 0 30px;
        }

        &__additional-labels {
            display: flex;
            align-items: center;
        }

        &__badges,
        &__authorization-hours {
            flex: 1;
            display: inline-block;
            margin-left: 5px;
            padding: 10px 20px 10px 40px;
            border-radius: 0;
            flex-wrap: wrap;
        }

        &__coursetest {
            display: inline;
            position: relative;
            margin: 0 10px 0 0;
            font-family: Nunito-ExtraBold;

            [class*="mobile_sm"] & {
                margin: 0;
            }

            [class*="desktop"] & {
                flex-direction: row;
            }
        }

        .icon-file-text {
            margin: 0 6px 0 0;
        }

        &.highlight {
            display: flex;
            font-size: 1.5rem;
            background: $heavy_metal;
            color: $white;
            flex-direction: column;
            pointer-events: all;

            [class*="desktop"] & {
                flex-direction: row;
                min-height: 55px;
                border-top: 1px solid $white;
            }

            [class*="icon-"] {
                display: inline;
                font-size: 2.4rem;
                color: $orange;
                position: relative;
                margin: 0 35px 0 0;

                &:before {
                    position: absolute;
                    transform: translateY(4px)
                }
            }

            .course-info__item {
                font-size: 1.8rem;
                padding: 10px 40px;
                border-bottom: 1px solid $white;
                margin: 0;

                [class*="desktop"] & {
                    border-right: 1px solid $white;
                    border-bottom: none;

                    &.-rating {
                        border: none;
                    }

                    &.-badges {
                        display: block;
                    }
                }
            }

            .course-info__label {
                font-size: 1.6rem;
            }

            .course-info__data {
                font-size: 1.6rem;
            }

            .course-info__additional-labels {
                padding: 25px 40px;

                [class*="desktop"] & {
                    margin-left: auto;
                }
            }

            .course-info__badges {
                border-bottom: 1px solid $white;
                [class*="desktop"] & {
                    border: none;
                }
            }
            .course-info__authorization-hours {
                padding: 10px 0 10px 40px;

                [class*="desktop"] & {
                    border-left: 1px solid $white;
                }
            }

            .course-info__rating-button {
                white-space: nowrap;
                position: relative;
                z-index: 1;
                max-height: 45px;

                &.pulse-animation {
                    &:before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        border-radius: 30px;
                        box-shadow: 0 0 0 0 rgba(0, 0, 0, 1);
                        transform: scale(1);
                        animation: pulse 2s;
                        animation-iteration-count: 3;
                        z-index: 0;
                    }
                }
            }

            @keyframes pulse {
                0% {
                    transform: scale(0.95);
                    box-shadow: 0 0 0 0 rgba(240, 125, 0, 0.7);
                }

                70% {
                    transform: scale(1.1);
                    box-shadow: 0 0 0 10px rgba(240, 125, 0, 0);
                }

                100% {
                    transform: scale(0.95);
                    box-shadow: 0 0 0 0 rgba(240, 125, 0, 0);
                }
            }
        }
    }
</style>
